import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  createStoryInvitationBodySchema,
  EXPIRATION_DAY_OPTIONS,
  PRODUCT_TYPES,
  type CreatedStoryInvitation,
  type ExpirationDays,
  type ProductTypeCode,
} from '@fois-utsavvoice/shared';
import { createStoryInvitation } from '../api/stories.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';
import { QrCard } from '../components/QrCard.js';

export function AdminCreateStoryPage() {
  const auth = useAuth();
  const [orderReference, setOrderReference] = useState('');
  const [productType, setProductType] = useState<ProductTypeCode>('GANESH_POOJA_BOX');
  const [productName, setProductName] = useState<string>(PRODUCT_TYPES[0].displayName);
  const [customerName, setCustomerName] = useState('');
  const [expirationDays, setExpirationDays] = useState<ExpirationDays>(90);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [created, setCreated] = useState<CreatedStoryInvitation | undefined>();
  const [copied, setCopied] = useState(false);

  function onProductTypeChange(code: ProductTypeCode): void {
    setProductType(code);
    const match = PRODUCT_TYPES.find((item) => item.code === code);
    if (match) {
      setProductName(match.displayName);
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!auth.tokens) {
      return;
    }
    const parsed = createStoryInvitationBodySchema.safeParse({
      productName,
      productType,
      orderReference: orderReference.trim() || undefined,
      customerName: customerName.trim() || undefined,
      expirationDays,
    });
    if (!parsed.success) {
      setError('Check the product details, then try again.');
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      setCreated(await createStoryInvitation(auth.tokens.idToken, parsed.data));
    } catch {
      setError('The story invitation could not be created.');
    } finally {
      setSubmitting(false);
    }
  }

  const customerUrl = created ? `${window.location.origin}${created.sharePath}` : '';

  return (
    <PageShell>
      <AdminNav />
      {created ? (
        <section className="card mx-auto max-w-2xl">
          <h1 className="heading-xl text-crimson">Story invitation created</h1>
          <p className="mt-3 text-charcoal">
            Add this unique link to a thank-you card, email, or WhatsApp message. The token is shown
            only once.
          </p>
          <label className="field-label mt-6" htmlFor="story-url">
            Customer URL
          </label>
          <textarea id="story-url" className="field-input" readOnly rows={3} value={customerUrl} />
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button
              className="btn-primary"
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(customerUrl);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <a className="btn-secondary" href={created.sharePath} target="_blank" rel="noreferrer">
              Open customer page
            </a>
            <Link className="btn-secondary" to={`/admin/stories/${created.storyId}`}>
              View story
            </Link>
          </div>
          <QrCard
            url={customerUrl}
            description="Print this code on a thank-you card. It encodes only the customer story URL. Download it now; the unique token is not stored after you leave this page."
            alt="QR code for the customer story URL"
            filenamePrefix="fois-utsavvoice-story"
          />
        </section>
      ) : (
        <section className="card mx-auto max-w-2xl">
          <h1 className="heading-xl text-crimson">Create story invitation</h1>
          <form className="mt-6 space-y-4" onSubmit={(event) => void onSubmit(event)}>
            <div>
              <label className="field-label" htmlFor="orderReference">
                Shopify order number (optional)
              </label>
              <input
                id="orderReference"
                className="field-input"
                value={orderReference}
                onChange={(event) => setOrderReference(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="productType">
                Box or product type
              </label>
              <select
                id="productType"
                className="field-input"
                value={productType}
                onChange={(event) => onProductTypeChange(event.target.value as ProductTypeCode)}
              >
                {PRODUCT_TYPES.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="productName">
                Display name
              </label>
              <input
                id="productName"
                required
                className="field-input"
                value={productName}
                onChange={(event) => setProductName(event.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="customerName">
                Customer name (optional)
              </label>
              <input
                id="customerName"
                className="field-input"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </div>
            <fieldset>
              <legend className="field-label">Link expiration</legend>
              <div className="flex flex-wrap gap-2">
                {EXPIRATION_DAY_OPTIONS.map((days) => (
                  <label
                    key={days}
                    className={`choice-chip ${expirationDays === days ? 'choice-chip-selected' : ''}`}
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="expirationDays"
                      value={days}
                      checked={expirationDays === days}
                      onChange={() => setExpirationDays(days)}
                    />
                    {days} days
                  </label>
                ))}
              </div>
            </fieldset>
            {error ? (
              <p className="text-sm font-semibold text-crimson" role="alert">
                {error}
              </p>
            ) : null}
            <button className="btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create story invitation'}
            </button>
          </form>
        </section>
      )}
    </PageShell>
  );
}
